/**
 * AI Service using Google Gemini API (Free Tier)
 * Provides explanations, ratings, and chatbot functionality
 */

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || 'YOUR_API_KEY_HERE';
const GEMINI_API_URL = 'https://generativelanguage.googleapis.com/v1/models/gemini-pro:generateContent';

class AIService {
  constructor() {
    this.apiKey = GEMINI_API_KEY;
  }

  /**
   * Generate explanation for why a system is recommended
   */
  async generateExplanation(recommendation, userParams) {
    const prompt = `You are an expert audio engineer at F&H Audio Solutions. 
    
User's Venue:
- Type: ${userParams.venueType}
- Dimensions: ${userParams.length}ft × ${userParams.width}ft × ${userParams.height}ft
- Capacity: ${userParams.capacity} people
- Usage: ${userParams.usage.join(', ')}
- Budget: ${userParams.budget}

Recommended System:
- ${recommendation.name}
- Speakers: ${recommendation.equipment.speakers.map(s => `${s.quantity}x ${s.brand} ${s.model}`).join(', ')}
- Mixer: ${recommendation.equipment.mixer.brand} ${recommendation.equipment.mixer.model}
- Total Investment: ₹${Math.round(recommendation.pricing.total / 1000)}K

Write a professional, customer-friendly explanation (3-4 sentences) of why this system is perfect for their venue. Focus on:
1. How it addresses their specific needs
2. Coverage and sound quality benefits
3. Value proposition

Keep it conversational but technical enough to show expertise.`;

    try {
      const response = await this.callGemini(prompt);
      return response || recommendation.whyRecommended;
    } catch (error) {
      console.error('AI explanation error:', error);
      // Fallback to template explanation
      return recommendation.whyRecommended;
    }
  }

  /**
   * Rank and rate multiple recommendations
   */
  async rankRecommendations(recommendations, userParams) {
    const prompt = `You are an audio system consultant. Rank these PA system recommendations for a client.

Client Requirements:
- Venue: ${userParams.venueType}
- Size: ${userParams.length}ft × ${userParams.width}ft (${userParams.length * userParams.width} sq.ft)
- Capacity: ${userParams.capacity} people
- Usage: ${userParams.usage.join(', ')}
- Budget: ${userParams.budget}

Recommendations:
${recommendations.map((r, i) => `
${i + 1}. ${r.name}
   - Equipment: ${r.equipment.speakers.length} speakers, ${r.equipment.mixer.brand} mixer
   - Cost: ₹${Math.round(r.pricing.total / 100000)} Lakhs
   - Coverage: ${r.technicalSpecs.coverageEfficiency}
   - Budget Category: ${r.budgetCategory}
`).join('\n')}

Rate each system from 1-3 stars based on:
- Best fit for requirements (3 stars = perfect match)
- Value for money
- Future scalability

Respond in JSON format:
{
  "rankings": [
    {"systemId": 1, "stars": 3, "reason": "brief reason"},
    {"systemId": 2, "stars": 2, "reason": "brief reason"},
    {"systemId": 3, "stars": 1, "reason": "brief reason"}
  ]
}`;

    try {
      const response = await this.callGemini(prompt);
      const parsed = JSON.parse(response);
      return parsed.rankings;
    } catch (error) {
      console.error('AI ranking error:', error);
      // Fallback to default ranking
      return recommendations.map((r, i) => ({
        systemId: i + 1,
        stars: 3 - i,
        reason: i === 0 ? 'Best overall match' : i === 1 ? 'Good alternative' : 'Budget option'
      }));
    }
  }

  /**
   * Chatbot conversation handler
   */
  async chatbotResponse(conversationHistory, userMessage) {
    // Check if API key is configured
    if (!this.isConfigured()) {
      return "I'm not fully configured yet. Please use the Quick Form option, or contact us directly at 7287809847 for personalized assistance.";
    }

    const systemPrompt = `You are an AI assistant for F&H Audio Solutions, a professional PA system design company in Guntur, India. 
    
Your role is to gather venue information to recommend sound systems. Ask ONE question at a time in a friendly, conversational manner.

Information you need to collect:
1. Venue type (church, auditorium, function hall, corporate, open ground)
2. Dimensions (length, width, height in feet)
3. Seating capacity (number of people)
4. Primary usage (speech, music, live band, DJ, etc.)
5. Budget range (economy, mid-range, premium)

Keep responses SHORT (1-2 sentences). Be warm and professional.`;

    const prompt = `${systemPrompt}

Conversation so far:
${conversationHistory.map(msg => `${msg.role}: ${msg.content}`).join('\n')}

User: ${userMessage}

Your response:`;

    try {
      const response = await this.callGemini(prompt);
      return response;
    } catch (error) {
      console.error('Chatbot error:', error);
      return "I apologize, but I'm having trouble connecting. Please try the form instead, or contact us at 7287809847.";
    }
  }

  /**
   * Extract structured data from chatbot conversation
   */
  async extractVenueData(conversationHistory) {
    const prompt = `Extract venue information from this conversation and return JSON.

Conversation:
${conversationHistory.map(msg => `${msg.role}: ${msg.content}`).join('\n')}

Return JSON format:
{
  "venueType": "church|auditorium|function_hall|corporate|open_ground",
  "length": number,
  "width": number,
  "height": number,
  "capacity": number,
  "usage": ["speech", "music", etc],
  "budget": "economy|mid-range|premium"
}

If any data is missing, use null. Convert all measurements to feet.`;

    try {
      const response = await this.callGemini(prompt);
      return JSON.parse(response);
    } catch (error) {
      console.error('Data extraction error:', error);
      return null;
    }
  }

  /**
   * Core method to call Gemini API
   */
  async callGemini(prompt) {
    const requestBody = {
      contents: [{
        parts: [{
          text: prompt
        }]
      }]
    };

    const response = await fetch(`${GEMINI_API_URL}?key=${this.apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Gemini API Error:', response.status, errorData);
      
      if (response.status === 404) {
        throw new Error('Invalid API key or API not enabled. Please verify your Gemini API key at https://makersuite.google.com/app/apikey');
      }
      
      throw new Error(`Gemini API error: ${response.status} - ${errorData?.error?.message || response.statusText}`);
    }

    const data = await response.json();
    
    if (data.candidates && data.candidates[0]?.content?.parts?.[0]?.text) {
      return data.candidates[0].content.parts[0].text.trim();
    }

    throw new Error('Invalid response from Gemini API');
  }

  /**
   * Check if API key is configured
   */
  isConfigured() {
    return this.apiKey && this.apiKey !== 'YOUR_API_KEY_HERE';
  }
}

// Export singleton instance
const aiService = new AIService();
export default aiService;
